import { useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_CURRENT_PROJECTS_PER_CLIENT } from '@bloomays-lib/adapter.api-talent';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { Role, type ApolloGqlJobFunnel, type ApolloGqlAccountManager } from '@bloomays-lib/types.shared';
import uniqBy from 'lodash/fp/uniqBy';
import compact from 'lodash/fp/compact';
import { useJobListStore } from '../stores/jobListStore';
import { Logger } from '../services/service-logger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';
import isEmpty from 'lodash/fp/isEmpty';
const logger = Logger('useJobList');

type GetCurrentProjectsResponse = {
  getCurrentProjectsPerClient: ApolloGqlJobFunnel[];
};

const buildTamList = (jobs: ApolloGqlJobFunnel[]): ApolloGqlAccountManager[] => {
  const accountManagers = compact(jobs.map((job) => job.accountManager)) as ApolloGqlAccountManager[];
  return uniqBy((am: ApolloGqlAccountManager) => am.email, accountManagers);
};

export const useJobList = () => {
  const auth = useAuthStore((state) => state.auth);
  const {
    jobs,
    setJobs,
    setLoading,
    setError,
    getJobById,
    mustRefreshJobList,
    sortAscending,
    setSortAscending,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    tamFilter,
    setTamFilter,
    tamList,
    setTamList,
  } = useJobListStore();

  // Empêche de ré-appliquer le filtre par défaut après une interaction utilisateur
  // (ex: sélectionner "Tous" ne doit pas re-sélectionner automatiquement l'utilisateur)
  const hasResolvedInitialTamFilterRef = useRef(false);

  // useLazyQuery ne se déclenche pas automatiquement
  const [fetchJobs, { data, loading, error, refetch }] = useLazyQuery<GetCurrentProjectsResponse>(
    GET_CURRENT_PROJECTS_PER_CLIENT,
    {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network', // on force le fetch pour éviter de récupérer les données en cache, si le mustRefreshJobList est true
    },
  );

  // Hydratation conditionnelle : fetch seulement si data in store n'est plus valide
  useEffect(() => {
    const shouldFetch = mustRefreshJobList();

    if (shouldFetch) {
      void fetchJobs();
    }
  }, [jobs, fetchJobs, mustRefreshJobList]);

  // Synchro loading
  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  // Synchro error
  useEffect(() => {
    if (error) {
      logger.error('useJobList > error graphql', {}, error);
      monitorException(error, { name: 'useJobList' });
      setError(error as Error);
    }
  }, [error, setError]);

  // Fallback : construire tamList depuis jobs persistés si tamList est vide
  useEffect(() => {
    if (isEmpty(tamList) && jobs && !isEmpty(jobs)) {
      setTamList(buildTamList(jobs));
    }
  }, [jobs, tamList, setTamList]);

  // Synchro data : mettre à jour jobs et tamList quand on reçoit de nouvelles data
  useEffect(() => {
    if (data?.getCurrentProjectsPerClient) {
      setJobs(data.getCurrentProjectsPerClient);
      setTamList(buildTamList(data.getCurrentProjectsPerClient));
    }
  }, [data, setJobs, setTamList]);

  // Initialisation du filtre TAM pour les admins :
  // - uniquement si l'utilisateur est authentifié
  // - qu'il a le rôle Admin
  // - et qu'il n'a pas le rôle System (les System doivent arriver par défaut sur "Tous")
  // - que tamFilter n'a pas encore été défini
  // - et que son email est présent dans la liste des TAM connus
  useEffect(() => {
    // Si tamFilter a déjà une valeur, on considère que l'initialisation est "résolue"
    // et on ne doit plus forcer un défaut plus tard.
    if (tamFilter !== null) {
      hasResolvedInitialTamFilterRef.current = true;
      return;
    }

    // Si on a déjà résolu l'init (auto ou utilisateur), ne rien faire.
    if (hasResolvedInitialTamFilterRef.current) {
      return;
    }

    if (
      auth?.isAuthenticated &&
      auth.user?.roles?.includes(Role.Admin) &&
      !auth.user?.roles?.includes(Role.System) &&
      auth.user.email &&
      tamFilter === null &&
      tamList.length > 0
    ) {
      const userEmail = auth.user.email;
      const userEmailInTamList = tamList.some((tam) => tam.email === userEmail);

      if (userEmailInTamList) {
        setTamFilter(userEmail);
        hasResolvedInitialTamFilterRef.current = true;
      }
    }
  }, [auth?.isAuthenticated, auth?.user?.email, auth?.user?.roles, tamFilter, tamList, setTamFilter]);

  // Fallback: si erreur réseau mais des données sont persistées en store, on les expose
  const effectiveLoading = loading && jobs === null;

  return {
    jobs,
    loading: effectiveLoading,
    error,
    refetch,
    getJobById,
    sortAscending,
    setSortAscending,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    tamFilter,
    setTamFilter,
    tamList,
  };
};

export default useJobList;
